import { getRovoParams } from '@atlaskit/rovo-triggers';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createResource } from '@townsquare/router/resources/createResource';
import type { UseResourceHookResponse } from '@townsquare/router/resources/types';
import { useResource } from '@townsquare/router/resources/useResource';

import { logMessage } from '@townsquare/logging';
import { getRelayEnvironment } from '@townsquare/relay-utils/townsquare-environment';
import type { HomeResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';

import { getRbacCheck } from './rbac-check';

export type RovoEntitlement = {
	isRovoEnabled: boolean;
	isRovoLLMEnabled: boolean;
	reason: string | null;
};

type EntitlementV2APIResponse = {
	isRovoEnabled: boolean;
	isRovoLLMEnabled: boolean;
	reason: string | null;
};

type RbacContextType = 'loading' | 'disabled' | 'enabled';

const RbacContext = createContext<RbacContextType>('loading');

type ProviderProps = {
	children: React.ReactNode;
};

/**
 * NoopRbacProvider is used in apps which do not have a supported app-level check in status.
 *
 * When NoopRbacProvider is used, useRovoEntitlement will only rely on the result from the `/assist/rovo/v2/entitlement` endpoint.
 */
export const NoopRbacProvider = ({ children }: ProviderProps) => {
	return <RbacContext.Provider value="enabled">{children}</RbacContext.Provider>;
};

type RbacProviderProps = {
	children: React.ReactNode;
	/**
	 * The UUID of the current workspace in which we should check RBAC.
	 */
	workspaceUuid?: string;
	/**
	 * Only projects and goals apps have supported backend checks for RBAC currently.
	 */
	app: 'projects' | 'goals';
};

/**
 * The RbacProvider is responsible for fetching the RBAC state for the current workspace from status.
 *
 * The status check aiPermittedForGoals/aiPermittedForProjects performs a check under the hood to confirm AI is enabled for the Projects
 * and Goals apps specifically.
 *
 * useRovoEntitlement checks both the RbacProvider from here AND the `/assist/rovo/v2/entitlement` endpoint.
 */
export const RbacProvider = ({ children, workspaceUuid, app }: RbacProviderProps) => {
	const [rbacState, setRbacState] = useState<RbacContextType>('loading');

	useEffect(() => {
		if (!fg('townsquare_enable_rbac_in_entitlement_check')) {
			setRbacState('enabled');
			return;
		}

		if (workspaceUuid) {
			getRbacCheck(workspaceUuid, app, getRelayEnvironment()).then(isRbacPermitted => {
				setRbacState(isRbacPermitted ? 'enabled' : 'disabled');
			});
		}
	}, [app, workspaceUuid]);

	return <RbacContext.Provider value={rbacState}>{children}</RbacContext.Provider>;
};

export const getTraceIdFromResponse = (response: Response | undefined) =>
	response?.headers?.get('x-trace-id') ??
	response?.headers?.get('atl-traceid') ??
	response?.headers?.get('x-b3-traceid') ??
	undefined;

const getRovoEntitlement = async (cloudId: string) => {
	try {
		const response = await fetch('/gateway/api/assist/rovo/v2/entitlement', {
			headers: {
				'X-Cloudid': cloudId ?? '',
			},
		});

		if (!response.ok) {
			const responseErrorMessage = await response.text().catch(() => undefined);
			logMessage('Failed to fetch RovoEntitlement [response not ok]', 'error', {
				cloudId,
				responseStatus: response.status,
				responseTraceId: getTraceIdFromResponse(response),
				responseErrorMessage,
			});

			return {
				isRovoEnabled: false,
				isRovoLLMEnabled: false,
				reason: 'Failed to fetch RovoEntitlement [response not ok]',
			};
		}

		const data: EntitlementV2APIResponse = await response.json();

		return {
			isRovoEnabled: data.isRovoEnabled,
			isRovoLLMEnabled: data.isRovoLLMEnabled,
			reason: data.reason,
		};
	} catch (error) {
		logMessage('Failed to fetch RovoEntitlement [catch block]', 'error', {
			cloudId,
			error,
		});

		return {
			isRovoEnabled: false,
			isRovoLLMEnabled: false,
			reason: 'Failed to fetch RovoEntitlement [catch block]',
		};
	}
};

export const fetchRovoEntitlement = async (cloudId: string | undefined | null) => {
	if (!cloudId) {
		return {
			isRovoEnabled: false,
			isRovoLLMEnabled: false,
			reason: 'No cloudId exists',
		};
	}

	return await getRovoEntitlement(cloudId);
};

type RovoEntitlementResponse = Pick<UseResourceHookResponse<RovoEntitlement>, 'loading' | 'data' | 'error'>;

/**
 * This hook relies on two checks:
 *
 * 1. The RBAC state from the RbacProvider if present. This is an app-level AND user-level check hydrated from status which tells us
 * 	whether AI is enabled for the user specifically in Projects/Goals. In Home/Teams we skip this with a NoopRbacProvider.
 *
 * 2. The result from the `/assist/rovo/v2/entitlement` endpoint. This is an site-level AND user-level check (not app-level), so it can
 * 	only tell us if the user has access to Rovo in some app in this site. This is the only perms check used in Home/Teams.
 *
 * Eventually we may split this so Projects/Goals only rely on (1), and Home/Teams only rely on (2).
 */
export const useRovoEntitlement = (): RovoEntitlementResponse => {
	const actualResource = useResource(rovoEntitlementResource);
	const rbacState = useContext(RbacContext);

	if (!fg('townsquare_enable_rbac_in_entitlement_check')) {
		return actualResource;
	}

	if (rbacState === 'loading') {
		return {
			loading: true,
			data: null,
			error: null,
		};
	}

	if (rbacState === 'disabled') {
		return {
			loading: false,
			data: {
				isRovoEnabled: false,
				isRovoLLMEnabled: false,
				reason: 'Rovo is disabled due to app permissions',
			},
			error: null,
		};
	}

	return actualResource;
};

export const rovoEntitlementResource = createResource<RovoEntitlement>({
	getData: async (_, resourceContext) => {
		const context = resourceContext as HomeResourceContext;
		const rovoParams = getRovoParams();
		// Workspace doesn't always exist on Atlassian Home **TODAY**, if we are rendering chat without a workspace, we need to default to the first cloudId (as they may still have sites)
		const cloudId = rovoParams.cloudId ?? context.collabContext?.rovoWorkspace;

		return await fetchRovoEntitlement(cloudId);
	},
	getKey: () => 'rovo-entitlement-resource-key',
	type: 'ROVO_ENTITLEMENT',
	maxAge: 60_000 * 30, // 30 minutes cache during the SPA session
});
